import React from 'react';
import styled, { css } from 'styled-components';

import { FormattedDateFormat, formatDateTime, getFormattedDate, isValidDate } from '~/util';

interface RelativeTimeProps {
  className?: string;
  timestamp: Date | string;
  format?: FormattedDateFormat;
  preventHover?: boolean;
  alignment?: 'left' | 'right';
}

const Relative = styled.div<{ alignment?: 'left' | 'right' }>`
  position: relative;
  opacity: 1;
  white-space: nowrap;
  transition: opacity 0.2s;
`;

export const Absolute = styled.div<{ alignment?: 'left' | 'right' }>`
  position: absolute;
  display: inline-block;
  top: 0;
  right: 0;
  white-space: nowrap;
  opacity: 0;
  z-index: 1;
  overflow: hidden;
  transition: opacity 0.2s;

  ${({ alignment }) =>
    alignment === 'left' &&
    css`
      left: 0;
      right: auto;
    `}

  &:before {
    content: '';
    position: absolute;
    right: 100%;
    top: 0;
    bottom: 0;
    width: 32px;
    background-image: linear-gradient(
      90deg,
      ${({ theme }) => theme.secondary.blendToBackground(50, 0)} 0%,
      ${({ theme }) => theme.secondary.blendToBackground(50, 1000)} 100%
    );

    ${({ theme }) =>
      theme.id !== 'default' &&
      css`
        background-image: linear-gradient(
          90deg,
          ${theme.secondary.blendToBackground(25, 0)} 0%,
          ${theme.secondary.blendToBackground(25, 1000)} 100%
        );
      `}

    ${({ alignment }) =>
      alignment === 'left' &&
      css`
        left: 100%;
        right: auto;
        background-image: linear-gradient(
          -90deg,
          ${({ theme }) => theme.secondary.blendToBackground(50, 0)} 100%,
          ${({ theme }) => theme.secondary.blendToBackground(50, 1000)} 0%
        );
      `}
  }
`;

const Wrapper = styled.div<{ preventHover?: boolean; alignment?: 'left' | 'right' }>`
  position: relative;
  font-size: 12px;
  line-height: 16px;
  height: 16px;
  display: block;

  ${({ preventHover }) =>
    !preventHover &&
    css`
      &:hover {
        ${Absolute} {
          opacity: 1;
          width: auto;
          overflow: visible;
          padding: 4px 8px 4px 12px;
          top: -4px;
          background: ${({ theme }) => theme.secondary.blendToBackground(50)};

          ${({ theme }) =>
            theme.id !== 'default' &&
            css`
              background-color: ${theme.secondary.blendToBackground(25)};
            `}
        }

        ${Relative} {
          opacity: 0;
        }
      }
    `}

  ${({ alignment }) =>
    alignment === 'left' &&
    css`
      &:hover {
        ${Absolute} {
          opacity: 0;
          left: 0;
          right: auto;
          padding: 0;
        }

        ${Relative} {
          opacity: 1;
        }
      }
    `};
`;

export const RelativeTime: React.FC<RelativeTimeProps> = ({
  className,
  timestamp,
  format = 'time-passed',
  preventHover = false,
  alignment = 'right',
}) => {
  return (
    <Wrapper className={className} preventHover={preventHover} alignment={alignment}>
      {!isValidDate(timestamp) ? (
        '-'
      ) : (
        <>
          {alignment === 'left' ? (
            <>
              <Relative alignment={alignment}>{formatDateTime(timestamp, { timeZoneFormat: 'utc' })}</Relative>
              <Absolute alignment={alignment}>{formatDateTime(timestamp, { timeZoneFormat: 'local' })}</Absolute>
            </>
          ) : (
            <>
              <Relative alignment={alignment}>{getFormattedDate(timestamp, format)}</Relative>
              <Absolute alignment={alignment}>{formatDateTime(timestamp, { timeZoneFormat: 'utc' })}</Absolute>
            </>
          )}
        </>
      )}
    </Wrapper>
  );
};
